<template>
  <VModal 
    v-model="displayModal"
    :closable="false"
    :hide-header="true"
    size="dialog"
    :dynamic-width="true"
    :hide-close-button="true"
    :no-padding="true"
    content-class="overflow-hidden"
  >
    <!-- Custom Content -->
    <div class="overflow-hidden">
      <!-- Platform-specific top color bar -->
      <div class="h-2" :class="{
        'bg-gradient-to-r from-blue-500 to-blue-700': platformId === 6, // Google
        'bg-gradient-to-r from-blue-400 to-indigo-600': platformId === 1, // Meta
        'bg-gradient-to-r from-black to-gray-800': platformId === 3, // X/Twitter
        'bg-gradient-to-r from-primary-500 to-primary-700': !platformId // Default
      }"></div>
      
      <div class="p-6">
        <!-- Platform Logo & Title -->
        <div class="flex items-center mb-6">
          <div class="mr-4 p-2 rounded-full" :class="{
            'bg-blue-50': platformId === 6, // Google
            'bg-blue-50': platformId === 1, // Meta
            'bg-gray-50': platformId === 3, // X/Twitter
            'bg-primary-50': !platformId // Default
          }">
            <img :src="platformIcon" :alt="platformName" class="w-12 h-12">
          </div>
          <div>
            <h2 class="text-xl font-bold text-gray-800">{{ title }}</h2>
            <p class="text-sm text-gray-500">{{ description }}</p>
          </div>
        </div>
        
        <!-- Status Indicators -->
        <div class="flex justify-center mb-6">
          <!-- Connecting animation - Dots loading -->
          <div v-if="status === 'connecting'" class="flex space-x-2">
            <div class="w-3 h-3 rounded-full bg-primary-500 animate-bounce" style="animation-delay: 0s"></div>
            <div class="w-3 h-3 rounded-full bg-primary-500 animate-bounce" style="animation-delay: 0.2s"></div>
            <div class="w-3 h-3 rounded-full bg-primary-500 animate-bounce" style="animation-delay: 0.4s"></div>
          </div>
          
          <!-- Success icon -->
          <div v-if="status === 'success'" class="flex justify-center">
            <div class="flex items-center justify-center w-20 h-20 rounded-full bg-green-100">
              <span class="material-symbols-outlined text-4xl text-green-500">check_circle</span>
            </div>
          </div>
          
          <!-- Error icon -->
          <div v-if="status === 'error'" class="flex justify-center">
            <div class="flex items-center justify-center w-20 h-20 rounded-full bg-red-100">
              <span class="material-symbols-outlined text-4xl text-red-500">error</span>
            </div>
          </div>
        </div>
        
        <!-- Error Message -->
        <div v-if="errorMessage" class="mb-6 p-4 border border-red-200 rounded-lg bg-red-50">
          <div class="flex">
            <span class="material-symbols-outlined text-red-500 mr-2">warning</span>
            <p class="text-red-600 text-sm">{{ errorMessage }}</p>
          </div>
        </div>
        
        <!-- Redirecting Text with Platform-colored Progress bar -->
        <div v-if="redirectingText" class="mt-6">
          <p class="text-sm text-gray-500 text-center mb-3">{{ redirectingText }}</p>
          
          <div v-if="status === 'connecting'" class="w-full h-1 bg-gray-200 rounded-full overflow-hidden">
            <div class="h-full animate-progress-bar" :class="{
              'bg-blue-500': platformId === 6, // Google
              'bg-blue-600': platformId === 1, // Meta
              'bg-black': platformId === 3, // X/Twitter
              'bg-primary-500': !platformId // Default
            }"></div>
          </div>
        </div>
      </div>
    </div>
  </VModal>
</template>

<script setup>
import { defineProps, computed, watch, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import VModal from '@/components/modals/VModal.vue';

const { t } = useI18n();

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false
  },
  platformId: {
    type: Number,
    default: null
  },
  status: {
    type: String,
    default: 'connecting', // 'connecting', 'success', 'error'
    validator: (value) => ['connecting', 'success', 'error'].includes(value)
  },
  errorMessage: {
    type: String,
    default: ''
  }
});

// Modal görünürlüğünü kontrol etmek için ref
const displayModal = ref(false);

// isVisible prop'unun değişimini izleyelim
watch(() => props.isVisible, (newValue) => {
  displayModal.value = newValue;
});

// Platform-specific data
const platformName = computed(() => {
  if (props.platformId === 1) return 'Meta';
  if (props.platformId === 3) return 'X';
  if (props.platformId === 6) return 'Google';
  return t('settings.components.integrations.platform');
});

const platformIcon = computed(() => {
  if (props.platformId === 1) return require('@/assets/icons/meta-logo.webp');
  if (props.platformId === 3) return require('@/assets/icons/twitterx.svg');
  if (props.platformId === 6) return require('@/assets/icons/google.svg');
  return '';
});

// Dynamic text based on status
const title = computed(() => {
  if (props.status === 'connecting') {
    return t('settings.components.integrations.connecting', { platformName: platformName.value });
  }
  if (props.status === 'success') {
    return t('settings.components.integrations.connected', { platformName: platformName.value });
  }
  if (props.status === 'error') {
    return t('settings.components.integrations.connectionFailed', { platformName: platformName.value });
  }
  return '';
});

const description = computed(() => {
  if (props.status === 'connecting') {
    return t('settings.components.integrations.pleaseWait');
  }
  if (props.status === 'success') {
    return t('settings.components.integrations.connectionSuccessful');
  }
  if (props.status === 'error') {
    return t('settings.components.integrations.connectionError');
  }
  return '';
});

const redirectingText = computed(() => {
  if (props.status === 'connecting') {
    return t('settings.components.integrations.redirectingShortly');
  }
  if (props.status === 'success') {
    return t('settings.components.integrations.redirectingNow');
  }
  return '';
});
</script>

<style scoped>
@keyframes progress {
  0% { width: 0%; }
  100% { width: 100%; }
}

.animate-progress-bar {
  animation: progress 2s ease-in-out infinite;
}

.animate-bounce {
  animation: bounce 1.2s infinite;
}

@keyframes bounce {
  0%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-8px); }
}
</style>